import { ReaderPresentation } from '../../shared/reader-presentation.component';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { PageDataService } from '../../core/page-data.service';
import { DynamicFormField } from '../forms/dynamic-form-fields.component';

export interface FortuneSaleCategory {
  categoryID: number;
  parentID?: number | null;
  formID: number;
  title: string;
  name?: string | null;
  slug: string;
  description?: string | null;
  metaTitle?: string | null;
  metaDescription?: string | null;
  imageUrl?: string | null;
  isFree?: boolean;
  minCreditPrice?: number;
  maxCreditPrice?: number;
  quickEnabled?: number | boolean;
  quickCreditPrice?: number | null;
  configured?: number | boolean;
  verifiedReviewCount?: number;
  verifiedReviewAverage?: number | null;
}
export interface FortuneSaleOffer {
  presentation?: ReaderPresentation | null;
  deliveryMinutes?: number | null;
  followUpCount?: number;
  followUpCreditPrice?: number;
  followUpWindowHours?: number;
  offerID: number;
  categoryID: number;
  sellerID: number;
  sellerName: string;
  shopName: string;
  description: string;
  creditPrice: number;
  profilePicture?: string | null;
  profileFocusX?: number | null;
  profileFocusY?: number | null;
  rating: number;
  reviewCount: number;
  orderCount?: number;
  followerCount?: number;
  rankingScore?: number;
  presenceStatus?: string | null;
}
export interface FortuneGuestGateway {
  id: number;
  method: string;
  title: string;
  settlementCurrency?: string | null;
  guestCheckoutEnabled: boolean;
  hosted?: boolean;
}
export interface FortuneCatalog {
  categories: FortuneSaleCategory[];
  offers: FortuneSaleOffer[];
  selectedCategory?: FortuneSaleCategory | null;
  formFields: DynamicFormField[];
  paymentGateways?: FortuneGuestGateway[];
}

@Injectable({ providedIn: 'root' })
export class FortuneCategoryService {
  private readonly api = inject(ApiClientService);
  private readonly pageData = inject(PageDataService);
  private catalogCache: FortuneCatalog | null = null;
  private catalogRequest: Promise<FortuneCatalog> | null = null;

  catalogSnapshot(): FortuneCatalog | null {
    const seeded = this.seededCatalog();
    if (seeded) this.catalogCache = seeded;
    return seeded ?? this.catalogCache;
  }

  async catalog(payload: Record<string, unknown> = {}): Promise<FortuneCatalog> {
    const rootRequest = !payload['categoryID'] && !payload['sellerID'] && !payload['sourceSlug'];
    if (rootRequest) {
      const snapshot = this.catalogSnapshot();
      if (snapshot) return snapshot;
      if (this.catalogRequest) return this.catalogRequest;
      this.catalogRequest = this.fetchCatalog(payload).then((catalog) => {
        this.catalogCache = catalog;
        return catalog;
      }).finally(() => {
        this.catalogRequest = null;
      });
      return this.catalogRequest;
    }
    return this.fetchCatalog(payload);
  }

  private async fetchCatalog(payload: Record<string, unknown>): Promise<FortuneCatalog> {
    const response = await firstValueFrom(this.api.post<any>('fortuneCategoryCatalog', payload));
    return response?.data ?? { categories: [], offers: [], formFields: [] };
  }

  private seededCatalog(): FortuneCatalog | null {
    const envelope = this.pageData.get<any>('fortuneCategories');
    const data = envelope?.data ?? envelope;
    return data && Array.isArray(data.categories) ? data as FortuneCatalog : null;
  }
  async order(
    offerID: number,
    answers: Record<string, unknown>,
    journeyToken = '',
    expectedCreditPrice?: number,
    expectedTerms?: Record<string, unknown>,
    expectedSellerID?: number,
    claimToken = '',
    payment?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const payload: Record<string, unknown> = {
      offerID,
      answers,
      journeyToken,
      claimToken,
      expectedCreditPrice,
      expectedTerms,
      ...(payment || {}),
    };
    if (journeyToken)
      payload['idempotencyKey'] =
        `fortune:${journeyToken}${payment?.['paymentGatewayID'] ? `:gateway:${payment['paymentGatewayID']}` : ''}`;
    const response = await firstValueFrom(this.api.post<any>('fortuneCategoryOrder', payload));
    return response?.data ?? {};
  }
  async quickOrder(
    categoryID: number,
    answers: Record<string, unknown>,
    journeyToken = '',
    expectedCreditPrice?: number,
    expectedTerms?: Record<string, unknown>,
    expectedSellerID?: number,
    claimToken = '',
    payment?: Record<string, unknown>,
  ): Promise<Record<string, unknown>> {
    const payload: Record<string, unknown> = {
      categoryID,
      quick: true,
      answers,
      journeyToken,
      claimToken,
      expectedCreditPrice,
      expectedTerms,
      expectedSellerID,
      ...(payment || {}),
    };
    if (journeyToken)
      payload['idempotencyKey'] =
        `fortune:${journeyToken}${payment?.['paymentGatewayID'] ? `:gateway:${payment['paymentGatewayID']}` : ''}`;
    const response = await firstValueFrom(this.api.post<any>('fortuneCategoryOrder', payload));
    return response?.data ?? {};
  }
  async quote(payload: Record<string, unknown>): Promise<any> {
    const response = await firstValueFrom(
      this.api.post<any>('/api/fortune-categories/quote', payload),
    );
    return response?.data?.quote ?? null;
  }
  async capturePaypal(checkoutID: number, paypalOrderID: string): Promise<Record<string, unknown>> {
    const response = await firstValueFrom(
      this.api.post<any>('paypalCapture', { checkoutID, paypalOrderID }),
    );
    return response?.data ?? {};
  }
  async followUps(payload: Record<string, unknown>): Promise<any> {
    const response = await firstValueFrom(this.api.post<any>('fortuneCategoryOrder', payload));
    return response?.data ?? {};
  }
  async trackJourney(payload: Record<string, unknown>): Promise<void> {
    await firstValueFrom(this.api.post<any>('fortuneCategoryJourney', payload));
  }
  async sellerData(): Promise<any> {
    const response = await firstValueFrom(this.api.post<any>('sellerFortuneCategoryData', {}));
    return response?.data ?? {};
  }
  async sellerSave(payload: Record<string, unknown>): Promise<any> {
    const response = await firstValueFrom(this.api.post<any>('sellerFortuneCategorySave', payload));
    return response?.data ?? {};
  }
  async adminData(): Promise<any> {
    const response = await firstValueFrom(this.api.post<any>('adminFortuneCategoryData', {}));
    return response?.data ?? {};
  }
  async adminSave(payload: Record<string, unknown>): Promise<any> {
    const response = await firstValueFrom(this.api.post<any>('adminFortuneCategorySave', payload));
    return response?.data ?? {};
  }
}
